<template>
  <UPage class="flex-1 flex flex-col min-h-0" :ui="{ center: 'flex flex-col flex-1 min-h-0' }">
    <UPageHeader title="Artikel" :description="tenantName ? `Mandant: ${tenantName}` : undefined">
      <template #links>
        <UButton
          to="/microtech-hub/products"
          color="neutral"
          icon="i-heroicons-arrow-left"
          label="Zurück"
          variant="ghost"
        />
        <UButton
          color="neutral"
          icon="i-heroicons-cog-6-tooth"
          label="Spalten"
          variant="outline"
          @click="openColumnSelector"
        />
        <UButton
          :loading="activeTab === 'artikel' ? loading : loadingDocs"
          color="primary"
          icon="i-heroicons-arrow-path"
          label="Aktualisieren"
          @click="onRefresh"
        />
      </template>
    </UPageHeader>

    <UPageBody class="p-0 sm:p-0 flex flex-col min-h-0 h-full">
      <UTabs
        v-model="activeTab"
        :items="tabs"
        class="flex-1 flex flex-col min-h-0 h-full"
        :ui="{ content: 'flex-1 flex flex-col min-h-0 h-full' }"
      >
        <template #artikel>
          <PortalTable
            ref="portalTable"
            :all-columns="allPossibleColumns"
            :columns-loading="loading && allPossibleColumns.length === 0"
            :data="products"
            :loading="loading"
            :selected-columns="selectedColumns"
            :sort-storage-key="SORT_STORAGE_KEY"
            :total="totalCount"
            empty-description="Es konnten keine Artikel für diesen Mandanten geladen werden."
            empty-icon="i-heroicons-shopping-cart"
            empty-title="Keine Artikel gefunden"
            class="h-full"
            @load-more="fetchProducts(true)"
            @reset:columns="resetToDefault"
            @select="onRowClick"
            @update:columns="updateColumns"
          />
        </template>

        <template #dokumente>
          <PortalTable
            ref="dokumenteTable"
            :all-columns="availableColumnsByTab.dokumente"
            :columns-loading="loadingDocs && availableColumnsByTab.dokumente.length === 0"
            :data="documents"
            :loading="loadingDocs"
            :selected-columns="selectedColumnsByTab.dokumente"
            :sort-storage-key="DOC_SORT_STORAGE_KEY"
            :total="totalCountDocs"
            highlight-row-key="fldGUID"
            :highlight-row-value="($route.query.highlightGUID as string)"
            empty-description="Es konnten keine Dokumente für diesen Mandanten geladen werden."
            empty-icon="i-heroicons-file-text"
            empty-title="Keine Dokumente gefunden"
            class="h-full"
            @load-more="fetchDocuments(true)"
            @reset:columns="resetToDefaultDocs"
            @select="(row) => downloadDocument(row.original)"
            @update:columns="updateDocColumns"
          />
        </template>
      </UTabs>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import { useProductStore, type Column } from '@/stores/product'
import * as introspection from '@/utils/graphql-introspection'
import { useAuthStore } from '@/stores/auth'
import { useDocumentDownload } from '@/composables/useDocumentDownload'

const authStore = useAuthStore()
const productStore = useProductStore()
const router = useRouter()
const route = useRoute()

const customerNo = computed(() => authStore.userInfo?.activeTenant?.customerNo || '')
const tenantName = computed(() => authStore.userInfo?.activeTenant?.tenantName || '')

const activeTab = ref(route.query.tab as string || 'artikel')
const tabs = [
  { label: 'Artikel', slot: 'artikel', value: 'artikel' },
  { label: 'Dokumente', slot: 'dokumente', value: 'dokumente' }
]
const loading = ref(false)
const products = ref<Record<string, unknown>[]>([])
const allPossibleColumns = ref<Array<{ key: string; label: string; graphql?: string }>>([])
const selectedColumns = ref<
  Array<{ key: string; label: string; locked?: boolean; graphql?: string }>
>([])
const portalTable = ref<InstanceType<typeof PortalTable> | null>(null)

const loadingDocs = ref(false)
const documents = ref<Record<string, unknown>[]>([])
const availableColumnsByTab = ref<Record<string, Column[]>>({
  dokumente: [],
})
const selectedColumnsByTab = ref<Record<string, Column[]>>({
  dokumente: [],
})
const dokumenteTable = ref<InstanceType<typeof PortalTable> | null>(null)

const endCursor = ref('')
const hasNextPage = ref(false)
const endCursorDocs = ref('')
const hasNextPageDocs = ref(false)
const limit = ref(1000)
const totalCount = ref(0)
const totalCountDocs = ref(0)

const STORAGE_KEY = computed(() => `cc_prod_cols_${customerNo.value}_${tenantName.value}`)
const DOC_STORAGE_KEY = computed(() => `cc_prod_doc_cols_${customerNo.value}_${tenantName.value}`)
const SORT_STORAGE_KEY = computed(() => `cc_prod_sort_${customerNo.value}_${tenantName.value}`)
const DOC_SORT_STORAGE_KEY = computed(() => `cc_prod_doc_sort_${customerNo.value}_${tenantName.value}`)

watch(activeTab, (val) => {
  router.replace({ query: { ...route.query, tab: val } })
  if (val === 'dokumente' && documents.value.length === 0) {
    fetchDocMetadata().then(() => fetchDocuments())
  }
})

watch(() => route.query.tab, (val) => {
  if (val && typeof val === 'string' && val !== activeTab.value) {
    activeTab.value = val
  }
})

async function fetchMetadata() {
  if (!customerNo.value || !tenantName.value) return
  try {
    const fields = await introspection.fetchTableFields(
      'tblProducts',
      customerNo.value,
      tenantName.value,
    )
    console.log('fetchMetadata fields received:', fields.length)

    const columns: Array<{ key: string; label: string; graphql?: string }> = []

    for (const f of fields) {
      if (
        f.name.startsWith('_') ||
        f.name.endsWith('_int') ||
        f.name.startsWith('tbl') ||
        f.name.startsWith('lnk')
      )
        continue

      if (f.name.startsWith('row')) {
        // Relationen auflösen (z.B. rowWgNr)
        if (f.name === 'rowWgNr') {
          const relFields = await introspection.fetchRelationFields(
            f.type,
            customerNo.value,
            tenantName.value,
          )
          const prefix = 'Warengruppe'
          relFields.forEach((rf) => {
            if (rf.isRelation || rf.name.startsWith('_')) return
            columns.push({
              key: `${f.name}.${rf.name}`,
              label: rf.label || `${prefix}: ${rf.name}`,
              graphql: rf.graphql ? `${f.name} { ${rf.graphql} }` : `${f.name} { ${rf.name} }`,
            })
          })
        }
        continue
      }

      columns.push({
        key: f.name,
        label: f.label || f.description || f.name,
        graphql: f.graphql,
      })
    }

    allPossibleColumns.value = columns

    // Update labels of selected columns if they were already loaded from store
    if (selectedColumns.value.length > 0) {
      selectedColumns.value = selectedColumns.value.map((sc) => {
        const found = columns.find((c) => c.key === sc.key)
        if (found) {
          return { ...sc, label: found.label, graphql: found.graphql }
        }
        return sc
      })
    }

    loadSavedColumns()
  } catch (error) {
    console.error('Fehler beim Laden der Metadaten:', error)
  }
}

function loadSavedColumns() {
  const saved = productStore.loadColumns(STORAGE_KEY.value)
  if (saved) {
    const parsed = [...saved]

    const updated = parsed.map((p) => {
      const found = allPossibleColumns.value.find((c) => c.key === p.key)
      if (found) {
        return { ...p, label: found.label, graphql: found.graphql }
      }
      return p
    })

    if (!updated.find((c: { key: string }) => c.key === 'fldArtNr')) {
      updated.unshift({ key: 'fldArtNr', label: 'Artikelnummer', locked: true })
    } else {
      const idx = updated.findIndex((c: { key: string }) => c.key === 'fldArtNr')
      updated[idx].locked = true
    }
    selectedColumns.value = updated
  } else {
    setDefaultColumns()
  }
}

function setDefaultColumns() {
  const defaults = [
    { key: 'fldArtNr', label: 'Artikelnummer', locked: true },
    { key: 'fldKuBez1', label: 'Kurzbezeichnung 1' },
    { key: 'fldSuchBeg', label: 'Suchbegriff' },
  ]

  const updatedDefaults = defaults.map((d) => {
    const found = allPossibleColumns.value.find((c) => c.key === d.key)
    if (found) {
      return { ...d, label: found.label, graphql: found.graphql }
    }
    return d
  })

  selectedColumns.value = updatedDefaults
  productStore.setColumns(STORAGE_KEY.value, updatedDefaults, false)
}

function loadSavedDocColumns() {
  const saved = productStore.loadColumns(DOC_STORAGE_KEY.value)
  if (saved) {
    const updated = (saved as Column[]).map((s) => {
      const found = availableColumnsByTab.value.dokumente.find((c) => c.key === s.key)
      return found ? { ...s, label: found.label, graphql: found.graphql } : s
    })
    selectedColumnsByTab.value.dokumente = updated
  } else {
    setDefaultDocColumns()
  }
}

function setDefaultDocColumns() {
  const defaults: Column[] = [
    { key: 'fldNr', label: 'Dokumentennummer' },
    { key: 'fldNa', label: 'Name' },
    { key: 'fldExt', label: 'Erweiterung' },
  ]
  const updatedDefaults = defaults.map((d) => {
    const found = availableColumnsByTab.value.dokumente.find((c) => c.key === d.key)
    if (found) {
      return { ...d, label: found.label, graphql: found.graphql }
    }
    return d
  })
  selectedColumnsByTab.value.dokumente = updatedDefaults
  productStore.setColumns(DOC_STORAGE_KEY.value, updatedDefaults, false)
}

function onRefresh() {
  if (activeTab.value === 'artikel') {
    fetchProducts(false)
  } else {
    fetchDocuments(false)
  }
}
function resetToDefault() {
  productStore.removeColumns(STORAGE_KEY.value)
  setDefaultColumns()
  products.value = []
  endCursor.value = ''
  fetchProducts()
}

function updateColumns(newCols: Array<{ key: string; label: string; locked?: boolean }>) {
  selectedColumns.value = newCols
  productStore.setColumns(STORAGE_KEY.value, newCols)
  products.value = []
  endCursor.value = ''
  fetchProducts()
}

function openColumnSelector() {
  if (activeTab.value === 'artikel') {
    portalTable.value?.openColumnSelector()
  } else {
    dokumenteTable.value?.openColumnSelector()
  }
}

async function fetchDocMetadata() {
  if (!customerNo.value || !tenantName.value) return
  try {
    const fields = await introspection.fetchRelationFields(
      'DocumentRowQueryRead',
      customerNo.value,
      tenantName.value,
    )
    const columns: Column[] = fields
      .filter((f) => !f.name.startsWith('_') && !f.name.endsWith('_int'))
      .map((f) => ({
        key: f.name,
        label: f.label || f.description || f.name,
        graphql: f.graphql || f.name,
      }))
    availableColumnsByTab.value.dokumente = columns
    loadSavedDocColumns()
  } catch (error) {
    console.error('Fehler beim Laden der Dokument-Metadaten:', error)
  }
}

function updateDocColumns(newCols: Column[]) {
  selectedColumnsByTab.value.dokumente = newCols
  productStore.setColumns(DOC_STORAGE_KEY.value, newCols)
  documents.value = []
  endCursorDocs.value = ''
  fetchDocuments()
}

function resetToDefaultDocs() {
  productStore.removeColumns(DOC_STORAGE_KEY.value)
  setDefaultDocColumns()
  documents.value = []
  endCursorDocs.value = ''
  fetchDocuments()
}

const { downloadDocument } = useDocumentDownload()

async function fetchDocuments(loadMore = false) {
  if (selectedColumnsByTab.value.dokumente.length === 0 || loadingDocs.value) return
  if (loadMore && (!hasNextPageDocs.value || !endCursorDocs.value)) return

  loadingDocs.value = true
  try {
    const selectionSet = buildSelectionSet(selectedColumnsByTab.value.dokumente)
    let currentAfter = loadMore && endCursorDocs.value ? endCursorDocs.value : ''
    let moreAvailable = true
    const allFetchedNodes: Record<string, unknown>[] = loadMore ? [...documents.value] : []

    while (moreAvailable) {
      const afterParam = currentAfter ? `after: "${currentAfter}"` : ''
      const query = `
        query {
          tblDocuments {
            conRead(
              first: ${limit.value},
              ${afterParam},
              fastFilter: { eq: [{ field: fldDBBkzBez }, { value: "Art" }] }
            ) {
              pageInfo {
                endCursor
                hasNextPage
                edgeCount
              }
              edges {
                node {
                  fldID
                  fldGUID
                  ${selectionSet}
                }
              }
            }
          }
        }
      `
      const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
      const conRead = response.data?.data?.tblDocuments?.conRead
      if (!conRead) {
        moreAvailable = false
        break
      }
      const newBatch = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      allFetchedNodes.push(...newBatch)
      currentAfter = conRead.pageInfo.endCursor
      moreAvailable = conRead.pageInfo.hasNextPage
      documents.value = [...allFetchedNodes]
      endCursorDocs.value = currentAfter
      hasNextPageDocs.value = moreAvailable
      totalCountDocs.value = conRead.pageInfo.edgeCount || documents.value.length
      if (newBatch.length === 0) moreAvailable = false
    }
  } catch (error) {
    console.error('Fehler beim Laden der Dokumente:', error)
  } finally {
    loadingDocs.value = false
  }
}

function onRowClick(row: { original: Record<string, unknown> }) {
  const rowData = row.original
  if (!rowData) return
  const artNr = getValue(rowData, 'fldArtNr')
  if (artNr) {
    router.push(`/microtech-hub/products/${artNr}`)
  }
}

async function fetchProducts(loadMore = false) {
  if (selectedColumns.value.length === 0 || loading.value) return
  if (loadMore && (!hasNextPage.value || !endCursor.value)) return

  loading.value = true
  try {
    const selectionSet = buildSelectionSet(selectedColumns.value)

    let currentAfter = loadMore && endCursor.value ? endCursor.value : ''
    let moreAvailable = true
    const allFetchedNodes: Record<string, unknown>[] = loadMore ? [...products.value] : []

    while (moreAvailable) {
      const afterParam = currentAfter ? `after: "${currentAfter}"` : ''
      const query = `
        query {
          tblProducts {
            conRead(first: ${limit.value}, ${afterParam}) {
              pageInfo {
                endCursor
                hasNextPage
                edgeCount
              }
              edges {
                node {
                  ${selectionSet}
                }
              }
            }
          }
        }
      `
      const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
      const conRead = response.data?.data?.tblProducts?.conRead

      if (!conRead) {
        console.error('Keine tblProducts.conRead Daten in der Antwort:', response.data)
        moreAvailable = false
        break
      }

      const newBatch = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      allFetchedNodes.push(...newBatch)

      currentAfter = conRead.pageInfo.endCursor
      moreAvailable = conRead.pageInfo.hasNextPage

      products.value = [...allFetchedNodes]
      endCursor.value = currentAfter
      hasNextPage.value = moreAvailable
      totalCount.value = conRead.pageInfo.edgeCount || products.value.length

      if (newBatch.length === 0) moreAvailable = false
    }
  } catch (error) {
    console.error('Fehler beim Laden der Artikel:', error)
  } finally {
    loading.value = false
  }
}

function buildSelectionSet(columns: Array<{ key: string; graphql?: string }>) {
  const customSelections = new Set<string>()
  const tree: Record<string, unknown> = {}

  columns.forEach((col) => {
    if (col.graphql && col.graphql.includes('{')) {
      customSelections.add(col.graphql)
    } else {
      const parts = (col.graphql || col.key).split('.')
      let current = tree as Record<string, unknown>
      parts.forEach((part) => {
        if (!current[part]) {
          current[part] = {} as Record<string, unknown>
        }
        current = current[part] as Record<string, unknown>
      })
    }
  })

  function stringify(obj: Record<string, unknown>): string {
    return Object.keys(obj)
      .map((k) => {
        const val = obj[k]
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
          const sub = stringify(val as Record<string, unknown>)
          return `${k} { ${sub} }`
        }
        return k
      })
      .join('\n')
  }

  const baseSelection = stringify(tree)
  return [baseSelection, ...Array.from(customSelections)].filter((s) => s).join('\n')
}

function getValue(obj: Record<string, unknown>, key: string) {
  return key.split('.').reduce((o: Record<string, unknown> | undefined, i) => {
    if (o && typeof o === 'object' && i in o) {
      return o[i] as Record<string, unknown> | undefined
    }
    return undefined
  }, obj)
}

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    if (activeTab.value === 'artikel') {
      await fetchMetadata()
      fetchProducts()
    } else if (activeTab.value === 'dokumente') {
      await fetchDocMetadata()
      fetchDocuments()
    }
  }

  watch([customerNo, tenantName], async ([newCust, newTen]) => {
    if (newCust && newTen) {
      if (activeTab.value === 'artikel') {
        await fetchMetadata()
        fetchProducts()
      } else if (activeTab.value === 'dokumente') {
        await fetchDocMetadata()
        fetchDocuments()
      }
    }
  })
})
</script>
